import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import AppHeader from '../components/layout/AppHeader';
import MyRewardPageHeader from '../components/bazzar/MyRewardPageHeader';
import MyRewardsTabSwitcher, { MY_REWARDS_TABS } from '../components/bazzar/MyRewardsTabSwitcher';
import RapchikRewardsPanel from '../components/bazzar/RapchikRewardsPanel';
import RedeemedCouponCard from '../components/bazzar/RedeemedCouponCard';
import { fetchRapchikRewards, fetchRedeemedCoupons } from '../services/bazaarService';
import { clearAuthSession } from '../storage/authStorage';
import { myRapchikRewardDetailPath, myRewardDetailPath } from '../routes/paths';

export default function MyRewardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(MY_REWARDS_TABS.rapchik);
  const [rapchikRewards, setRapchikRewards] = useState({ totalWinnings: 0, items: [] });
  const [coupons, setCoupons] = useState([]);
  const [rapchikLoading, setRapchikLoading] = useState(true);
  const [couponsLoading, setCouponsLoading] = useState(true);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const loadRapchikRewards = useCallback(async () => {
    setRapchikLoading(true);

    try {
      const data = await fetchRapchikRewards();
      setRapchikRewards(data);
    } catch {
      setRapchikRewards({ totalWinnings: 0, items: [] });
    } finally {
      setRapchikLoading(false);
    }
  }, []);

  const loadCoupons = useCallback(async () => {
    setCouponsLoading(true);

    try {
      const items = await fetchRedeemedCoupons();
      setCoupons(items);
    } catch {
      setCoupons([]);
    } finally {
      setCouponsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRapchikRewards();
    loadCoupons();
  }, [loadRapchikRewards, loadCoupons]);

  const handleAccessCoupon = (coupon) => {
    navigate(myRewardDetailPath(coupon.id));
  };

  const handleConfirmLogout = () => {
    clearAuthSession();
    window.location.href = '/';
  };

  const isRapchikTab = activeTab === MY_REWARDS_TABS.rapchik;
  const tabLoading = isRapchikTab ? rapchikLoading : couponsLoading;

  return (
    <MainLayout>
      <Box sx={{ px: { md: '50px' } }}>
        <AppHeader endVariant="profile" onLogoutClick={() => setLogoutDialogOpen(true)} />
      </Box>

      <Box sx={{ px: { xs: 1.25, sm: 2, md: 3 }, pb: 6, pt: { xs: 1, md: 2 } }}>
        <MyRewardPageHeader title="My Rewards" />

        <MyRewardsTabSwitcher value={activeTab} onChange={setActiveTab} />

        {tabLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#ff00f2' }} />
          </Box>
        ) : isRapchikTab ? (
          <RapchikRewardsPanel
            data={rapchikRewards}
            onRevealPrize={(item) => navigate(myRapchikRewardDetailPath(item.id))}
          />
        ) : coupons.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Typography sx={{ color: '#e6d4ff', fontSize: 16, fontWeight: 600, textAlign: 'center' }}>
              No redeemed coupons yet.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: '10px', md: 3 },
              rowGap: { xs: '28px', md: 3 },
              flexWrap: 'wrap',
            }}
          >
            {coupons.map((coupon) => (
              <RedeemedCouponCard key={coupon.id} coupon={coupon} onAccessCoupon={handleAccessCoupon} />
            ))}
          </Box>
        )}
      </Box>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#1a0834',
            border: '1px solid #ff66f766',
            color: '#fff',
            minWidth: { xs: '90%', sm: 380 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#fff' }}>Logout</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#e6d4ff' }}>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ color: '#e6d4ff', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #822AFF 0%, #FF00F2 100%)',
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
